<script setup lang="ts">
import { patchesRest, readOrNull } from '@/lib/api';
import { autocomplete } from '@algolia/autocomplete-js';
import '@algolia/autocomplete-theme-classic';
import { formatDistanceToNow } from 'date-fns';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { classes } from './AppSearchConfig';

interface SearchBarProps {
  url: string;
  index?: string;
  searchConstraints?: URLSearchParams;
  /** Autofocus the input on mount. Off for always-visible placements (e.g. the
   * user page) so it doesn't steal focus on every load; on for Home / the
   * toggle-to-open data-column search. */
  autoFocus?: boolean;
}

type BaseSearchItem = Record<string, unknown>;

interface UserItem extends BaseSearchItem {
  id?: string;
  name?: string;
  email?: string;
  lastOnline?: number;
}

interface ProjectItem extends BaseSearchItem {
  id?: string;
  title?: string;
  author?: string;
  modified?: number;
}

interface SearchResponse {
  results: { docId: string; data: BaseSearchItem }[];
}

const emit = defineEmits(['close']);
const props = withDefaults(defineProps<SearchBarProps>(), {
  index: 'users',
  searchConstraints: undefined,
  autoFocus: true,
});

const router = useRouter();

const searchInput = ref<HTMLDivElement | null>(null);
const loading = ref(false);
const searchConstraints = ref<URLSearchParams | undefined>(props.searchConstraints);
const queryString = ref(searchConstraints.value ? '?' + searchConstraints.value.toString() : '');

function updateConstraints() {
  searchConstraints.value = new URLSearchParams(queryString.value);
}

function goto(url: string) {
  router.push(url);
}

let instance: ReturnType<typeof autocomplete> | null = null;

onMounted(() => {
  if (!searchInput.value) return;

  const inst = autocomplete({
    container: searchInput.value,
    placeholder: `Search for ${props.index}`,
    initialState: { activeItemId: 0, query: queryString.value || '' },
    autoFocus: props.autoFocus,
    defaultActiveItemId: 0,
    onStateChange({ state }) {
      if (state.activeItemId === null) {
        emit('close');
      } else if (state.query === '') {
        searchConstraints.value = undefined;
      }
    },
    navigator: {
      navigate({ itemUrl }) {
        inst.setIsOpen(false);
        emit('close');
        goto(itemUrl);
      },
    },
    getSources({ navigator }) {
      return [
        {
          sourceId: props.index,
          async getItems(params: { query: string }) {
            if (params.query.startsWith('?')) {
              queryString.value = params.query;
              setTimeout(() => inst.setIsOpen(false), 0);
              return [];
            }
            if (params.query.length < 3) return [];
            searchConstraints.value = undefined;
            loading.value = true;
            // Tolerant read: a failed search resolves to [] (old behavior) rather
            // than rejecting the Autocomplete source and leaving the spinner stuck.
            const data = await readOrNull(
              patchesRest.get(`/docs/${props.index}/_search`).query('q', params.query).send<SearchResponse>()
            );
            loading.value = false;
            const results = (data?.results ?? []) as SearchResponse['results'];
            return results.map(({ docId, data }) => ({
              ...data,
              id: docId.slice(docId.indexOf('/') + 1),
            })) as (UserItem | ProjectItem)[];
          },
          onSelect({ item, itemUrl, state }) {
            if (!itemUrl) return;
            navigator.navigate({ item, itemUrl, state });
          },
          getItemUrl({ item }: { item: UserItem & ProjectItem }): string | undefined {
            if (item.id) {
              return props.url.replace(':id', String(item.id));
            }
          },
          templates: {
            item: ({ item, html }) => {
              const isUser = props.index === 'users';
              const u = item as UserItem;
              const p = item as ProjectItem;
              return html`
                <div class="meta">
                  <div class="id">${item.id || ''}</div>
                  <div class="date">
                    <span
                      >${isUser
                        ? Number(u.lastOnline) > 0
                          ? formatDistanceToNow(Number(u.lastOnline))
                          : ''
                        : Number(p.modified) > 0
                          ? formatDistanceToNow(Number(p.modified))
                          : ''}</span
                    >
                  </div>
                </div>
                <div class="name">${isUser ? u.name || '' : p.title || 'Untitled Novel'}</div>
                <div class="subtext">${isUser ? u.email || '' : p.author || 'Unknown Author'}</div>
              `;
            },
            noResults({ state, html }) {
              if (state.status === 'loading' || state.status === 'stalled') {
                return html`<div class="searching">Searching…</div>`;
              }
              return html`<div class="empty">(No matches)</div>`;
            },
          },
        },
      ];
    },
  });

  // Vue ignores a value returned from onMounted, so the teardown has to be
  // registered explicitly or the Autocomplete instance outlives the component.
  instance = inst;
});

onBeforeUnmount(() => {
  instance?.destroy();
  instance = null;
});
</script>

<template>
  <section class="app-search">
    <div :class="classes()" ref="searchInput" @keydown.enter="updateConstraints"></div>
  </section>
</template>

<style lang="scss">
.app-search {
  width: 100%;
}

.aa-Panel {
  --aa-spacing-half: 0.125rem;
  --aa-panel-shadow: 0 0 0 1px rgba(35, 38, 59, 0.1), 0 4px 16px -3px rgba(35, 38, 59, 0.25);

  .empty {
    color: red !important;
  }

  .searching {
    color: #999 !important;
  }

  .aa-Item {
    position: relative;
    padding: 0.625rem 0.875rem;
    margin: 0.25rem 0.5rem;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
  }

  .meta {
    display: flex;
    flex-direction: row;
    align-items: center;
    overflow: hidden;
  }

  .id {
    font-size: 10px;
    color: #999;
    flex: 1 1 0px;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .date {
    font-size: 12px;
    color: #999;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .name {
    font-size: 16px;
    font-weight: 600;
    margin: 0.2rem 0;
  }

  .subtext {
    font-size: 12px;
    color: #007bff;
  }
}
</style>
